/* @pareto-engineering/generator-front 1.0.12 */
import * as React from 'react'

import { useRef } from 'react'

import { Link } from 'react-router-dom'

import PropTypes from 'prop-types'

import styleNames from '@pareto-engineering/bem/exports'

import format from 'date-fns/format'

import {
  Title,
  ContentTree,
  SocialMediaShareButton,
  Button,
} from '@pareto-engineering/design-system'

import { TimeToRead, ConversionFormButton } from 'modules/components'

import { useBlog } from '../Context'

import './styles.scss'

// Local Definitions
const baseClassName = styleNames.base

const componentClassName = 'blog-post'

/**
 * This is the component description.
 */
const Post = ({
  id,
  className:userClassName,
  style,
  title,
  description,
  content,
  category,
  ctaText,
  author,
  publishedOn,
  mainImage,
  selectors,
  wordCount,
  // ...otherProps
}) => {
  const {
    generateBlogPath,
  } = useBlog()

  const url = category && generateBlogPath(
    'LIST_CATEGORY',
    {
      category:category?.slug,
    },
  )

  const ref = useRef(null)

  return (
    <div
      id={id}
      className={[
        baseClassName,
        componentClassName,
        userClassName,
        'grid dense',
      ]
        .filter((e) => e)
        .join(' ')}
      style={style}
      // {...otherProps}
    >
      <Title
        className="span-8 md-span-12"
        heading={title}
      />

      <div className="post-info span-8 md-span-12">
        <div className="post-sub">
          {
            author?.firstName && (
              <div
                className="author"
                itemProp="author"
                itemScope
                itemType="http://schema.org/Person"
              >
                <span
                  itemProp="givenName"
                >
                  {author.firstName}
                </span>
                {' '}
                <span
                  itemProp="familyName"
                >
                  {author.lastName}
                </span>
              </div>
            )
          }
          <div className="published-on">
            { format(
              new Date(publishedOn),
              'MMMM do, yyyy',
            ) }
          </div>
          <div className="buttons">
            {wordCount && (
              <TimeToRead
                wordCount={wordCount}
                color="subtitle"
              />
            )}
          </div>
        </div>
        <div className="post-sub">
          {
            category?.name && (
              <Link
                to={url}
                style={{ textDecoration: 'none' }}
              >
                <Button
                  isCompact
                  color="main2"
                  className="category"
                >
                  {category.name}
                </Button>
              </Link>
            )
          }
          <div className="social-media-buttons">
            <SocialMediaShareButton type="facebook" />
            <SocialMediaShareButton type="twitter" />
          </div>
        </div>
      </div>

      <div className="span-8 md-span-12 illustration-container">
        <img
          src={mainImage}
          alt={description || title}
          itemProp="image"
        />
      </div>

      <ContentTree
        key={title}
        className="md-start-10 md-span-3"
        target={ref}
        selectors={selectors}
      />

      <div
        className="article-body s1 span-8 md-span-8"
        ref={ref}
        dangerouslySetInnerHTML={{ __html: content }}
      />

      {
        ctaText && (
          <div className="span-8 md-span-7 s1">
            <ConversionFormButton
              isSimple
            >
              {ctaText}
            </ConversionFormButton>
          </div>
        )
      }
    </div>
  )
}

Post.propTypes = {
  /**
   * The HTML id for this element
   */
  id:PropTypes.string,

  /**
   * The HTML class names for this element
   */
  className:PropTypes.string,

  /**
   * The React-written, css properties for this element.
   */
  style:PropTypes.objectOf(PropTypes.string),

  /**
   * The title of this blog post
   */
  title:PropTypes.string.isRequired,

  /**
   * The description of this blog post
   */
  description:PropTypes.string.isRequired,

  /**
   * The main image of this blog post
   */
  mainImage:PropTypes.string.isRequired,

  /**
   * The content of this blog post
   */
  content:PropTypes.string.isRequired,

  /**
   * The word count of this blog post
   */
  wordCount:PropTypes.number.isRequired,

  /**
   * The first and last name of the article's author
   */
  // author:PropTypes.shape({
  //   firstName:PropTypes.string.isRequired,
  //   lastName :PropTypes.string.isRequired,
  // }).isRequired,

  /**
   * The date this blog post was published
   */
  publishedOn:PropTypes.string.isRequired,

  /**
   * The category of this blog post
   */
  category:PropTypes.node.isRequired,

  /**
   * The selectors for the content tree
   */
  selectors:PropTypes.arrayOf(PropTypes.string),
}

Post.defaultProps = {
  selectors:['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
}

export default Post
